import { gql } from '@apollo/client';
import { apolloClient } from '../config/apollo-client';

// GraphQL mutation for creating a user
export const CREATE_USER_MUTATION = gql`
  mutation CreateUser($createUserInput: CreateUserInput!) {
    createUser(createUserInput: $createUserInput) {
      id
      username
    }
  }
`;

// Types for creating users
export interface CreateUserInput {
  username: string;
  name: string;
  password: string;
  host?: string;
  role: string;
  phone?: string;
  parentId?: number;
  email?: string;
  comment?: string;
  avatarUrl?: string;
  language?: string;
  timezone?: string;
}

export interface CreateUserResponse {
  id: number;
  username: string;
}

// CreateUsers service class
export class CreateUsersService {
  private static instance: CreateUsersService;

  private constructor() {}

  public static getInstance(): CreateUsersService {
    if (!CreateUsersService.instance) {
      CreateUsersService.instance = new CreateUsersService();
    }
    return CreateUsersService.instance;
  }

  // Create distributor method
  async createDistributor(
    userData: CreateUserInput,
  ): Promise<CreateUserResponse> {
    try {
      const response = await apolloClient.mutate({
        mutation: CREATE_USER_MUTATION,
        variables: { createUserInput: userData },
      });
      if (response.errors && response.errors.length > 0) {
        throw response;
      }
      if (!response.data || !response.data.createUser) {
        throw new Error('No data returned from create distributor mutation');
      }
      return response.data.createUser as CreateUserResponse;
    } catch (error: any) {
      console.error('Error creating distributor:', error);
      // Log more detailed error information
      if (error.graphQLErrors) {
        error.graphQLErrors.forEach((err: any) => {
          console.error('GraphQL Error:', err.message);
          console.error('Locations:', err.locations);
          console.error('Path:', err.path);
        });
      }
      if (error.networkError) {
        console.error('Network Error:', error.networkError);
      }
      throw error;
    }
  }

  // Generic create user method
  async createUser(userData: CreateUserInput): Promise<CreateUserResponse> {
    try {
      const response = await apolloClient.mutate({
        mutation: CREATE_USER_MUTATION,
        variables: { createUserInput: userData },
      });
      if (response.errors && response.errors.length > 0) {
        throw response;
      }
      if (!response.data || !response.data.createUser) {
        throw new Error('No data returned from create user mutation');
      }
      return response.data.createUser as CreateUserResponse;
    } catch (error: any) {
      console.error('Error creating user:', error);
      if (error.graphQLErrors) {
        error.graphQLErrors.forEach((err: any) => {
          console.error('GraphQL Error:', err.message);
          console.error('Locations:', err.locations);
          console.error('Path:', err.path);
        });
      }
      if (error.networkError) {
        console.error('Network Error:', error.networkError);
      }
      throw error;
    }
  }
}

// Export singleton instance
export const createUsersService = CreateUsersService.getInstance();

// Export convenience function for backward compatibility
export async function createDistributor(
  userData: CreateUserInput,
): Promise<CreateUserResponse> {
  return createUsersService.createDistributor(userData);
}
